import { WorkbenchDto, CreateWorkbenchDto, UpdateWorkbenchDto, UpsertWorkbenchGeneratorInstanceDto, SubmittedUpsertWorkbenchGeneratorInstanceDto } from '@services/dtos'
import { baseCrudReducer } from './baseCrudReducer'
import { workbenchService, WorkbenchFilters } from '@services/workbenchService'
import { combineReducers, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { castDraft } from 'immer'
import type { RootState } from '@state/store'
import { logout } from '../thunks/authThunk'

const SESSION_KEY = 'currentWorkbenchId'
const name = 'workbench'

const { reducer: crudReducer, actions, thunks } = baseCrudReducer<
  WorkbenchDto,
  CreateWorkbenchDto,
  UpdateWorkbenchDto,
  WorkbenchFilters
>(name, workbenchService, { selectFilters: (state) => state.workbench.crud.filters })

const fetchCurrentWorkbench = createAsyncThunk(
  `${name}/fetch-current`,
  async (id: string) => workbenchService.getOne(id)
)

const upsertGeneratorInstance = createAsyncThunk(
  `${name}/upsert-generator-instance`,
  async ({ id, generatorSlotId, dto }: { 
    id: string; generatorSlotId: string; dto: SubmittedUpsertWorkbenchGeneratorInstanceDto 
  }, { dispatch }) => {
    const result = await workbenchService.upsertGeneratorInstance(id, generatorSlotId, dto)
    dispatch(thunks.getAll())
    return result
  }
)

const removeGeneratorInstance = createAsyncThunk(
  `${name}/remove-generator-instance`,
  async ({ id, generatorSlotId }: { id: string; generatorSlotId: string }, { dispatch }) => {
    const result = await workbenchService.removeGeneratorInstance(id, generatorSlotId)
    dispatch(thunks.getAll())
    return result
  }
)

const userWorkbenchSlice = createSlice({
  name: 'userWorkbench',
  initialState: {
    currentWorkbenchId: sessionStorage.getItem(SESSION_KEY) ?? null as string | null,
    currentWorkbench: null as WorkbenchDto | null,
    isLoadingCurrentWorkbench: false,
  },
  reducers: {
    setCurrentWorkbenchId(state, action: PayloadAction<string | null>) {
      if (state.currentWorkbenchId === action.payload) {
        return
      }
      state.currentWorkbenchId = action.payload
      state.currentWorkbench = null
      if (action.payload) {
        sessionStorage.setItem(SESSION_KEY, action.payload)
      } else {
        sessionStorage.removeItem(SESSION_KEY)
      }
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchCurrentWorkbench.pending, (state) => {
      state.isLoadingCurrentWorkbench = true
    })
    b.addCase(fetchCurrentWorkbench.fulfilled, (state, action) => {
      state.currentWorkbench = castDraft(action.payload)
      state.isLoadingCurrentWorkbench = false
    })
    b.addCase(fetchCurrentWorkbench.rejected, (state) => {
      state.isLoadingCurrentWorkbench = false
    })
    b.addCase(thunks.remove.fulfilled, (state, action) => {
      if (state.currentWorkbenchId === action.payload) {
        state.currentWorkbenchId = null
        state.currentWorkbench = null
        sessionStorage.removeItem(SESSION_KEY)
      }
    })
    b.addCase(logout.fulfilled, (state) => {
      state.currentWorkbenchId = null
      state.currentWorkbench = null
      sessionStorage.removeItem(SESSION_KEY)
    })
    b.addCase(thunks.update.fulfilled, (state, action) => {
      if (state.currentWorkbenchId === action.payload.id) {
        state.currentWorkbench = castDraft(action.payload)
      }
    })
    b.addCase(upsertGeneratorInstance.fulfilled, (state, action) => {
      if (state.currentWorkbenchId === action.payload.id) {
        state.currentWorkbench = castDraft(action.payload)
      }
    })
    b.addCase(removeGeneratorInstance.fulfilled, (state, action) => {
      if (state.currentWorkbenchId === action.payload.id) {
        state.currentWorkbench = castDraft(action.payload)
      }
    })
  },
})

export default combineReducers({
  crud: crudReducer,
  userWorkbench: userWorkbenchSlice.reducer,
})

export const selectCurrentWorkbenchId = (state: RootState) =>
  state.workbench.userWorkbench.currentWorkbenchId

export const selectCurrentWorkbench = (state: RootState) =>
  state.workbench.userWorkbench.currentWorkbench

export const selectIsLoadingCurrentWorkbench = (state: RootState) =>
  state.workbench.userWorkbench.isLoadingCurrentWorkbench

export const { setFilters: setWorkbenchFilters } = actions
export const { setCurrentWorkbenchId } = userWorkbenchSlice.actions

export const {
  getAll: getWorkbenches,
  create: createWorkbench,
  update: updateWorkbench,
  remove: removeWorkbench,
} = thunks
export { upsertGeneratorInstance, removeGeneratorInstance, fetchCurrentWorkbench }
